import type { QuestionnaireResponseItemAnswer } from "@medplum/fhirtypes";

import { LOINCAnswerCodes } from "@/fhir/enums/loinc";
import { Systems } from "@/fhir/enums/systems";
import {
  NO_DISPLAY,
  YES_DISPLAY,
  type YesNoDisplay,
  type YesNoLoincCoding,
} from "@/intake-forms/constants/yes-no-display";

/** `null` means the input could not be mapped to a Yes/No LOINC coding. */
export function yesNoDisplayToLoincCoding(
  display: YesNoDisplay | null | undefined,
): YesNoLoincCoding | null {
  if (display === YES_DISPLAY) {
    return {
      system: Systems.LOINC,
      code: LOINCAnswerCodes.Yes,
      display: YES_DISPLAY,
    };
  }
  if (display === NO_DISPLAY) {
    return {
      system: Systems.LOINC,
      code: LOINCAnswerCodes.No,
      display: NO_DISPLAY,
    };
  }
  return null;
}

/** `null` means the LOINC code is not a Yes/No answer. */
export function loincCodingToYesNoDisplay(code: string | undefined): YesNoDisplay | null {
  if (code === LOINCAnswerCodes.Yes) {
    return YES_DISPLAY;
  }
  if (code === LOINCAnswerCodes.No) {
    return NO_DISPLAY;
  }
  return null;
}

function isMissingOrLoincSystem(system: string | undefined): boolean {
  return system === undefined || system === Systems.LOINC;
}

/**
 * Reads Yes/No display from a questionnaire answer.
 *
 * Precedence (first match wins):
 * 1. valueCoding.code with missing system or LOINC: LOINC Yes/No codes
 * 2. valueCoding.code: legacy "Yes"/"No" codes (any system)
 * 3. valueCoding.display: "Yes"/"No"
 * 4. valueString: "Yes"/"No"
 */
export function readYesNoDisplayFromAnswer(
  answer: QuestionnaireResponseItemAnswer | undefined,
): YesNoDisplay | null {
  if (!answer) {
    return null;
  }

  const coding = answer.valueCoding;
  if (coding?.code && isMissingOrLoincSystem(coding.system)) {
    const fromLoinc = loincCodingToYesNoDisplay(coding.code);
    if (fromLoinc) {
      return fromLoinc;
    }
  }

  if (coding?.code === YES_DISPLAY || coding?.code === NO_DISPLAY) {
    return coding.code;
  }

  if (coding?.display === YES_DISPLAY || coding?.display === NO_DISPLAY) {
    return coding.display;
  }

  if (answer.valueString === YES_DISPLAY || answer.valueString === NO_DISPLAY) {
    return answer.valueString;
  }

  return null;
}

/** True when a questionnaire answer normalizes to "Yes". */
export function isYesAnswer(answer: QuestionnaireResponseItemAnswer | undefined): boolean {
  return readYesNoDisplayFromAnswer(answer) === YES_DISPLAY;
}
